import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { Row, Col, Button, Skeleton } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';

import { EmptyReleaseNote } from 'common/components/svgs';
import CreateReleaseModal from './modal';
import ViewReleaseModal from './modal/view';
import Card from './card';

import {
  QUERY_RELEASE_NOTES,
  MUTATION_DELETE_RELEASE_NOTE,
  MUTATION_CHANGE_PUBLISHED_AT,
} from './queries';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    ...theme.typography.h2,
    color: theme.colors.titleColor,
    marginBottom: 0,
  },
  content: {
    flex: 1,
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    borderRadius: 2,
    backgroundColor: theme.colors.gray1,
  },
  emptySummary: {
    ...theme.typography.body2,
    color: theme.colors.titleColor,
    marginTop: 24,
    marginBottom: 24,
  },
  skeleton: {
    padding: '8px 24px',
    borderRadius: 2,
    marginBottom: 16,
    backgroundColor: theme.colors.gray1,

    '& .ant-skeleton-title': {
      marginBottom: 0,
    },

    '& .ant-skeleton-paragraph': {
      marginTop: '16px !important',
      marginBottom: '16px !important',
    },
  },
  articleList: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  articleItem: {
    width: 'calc(50% - 9.5px)',

    '&:nth-child(2n+1)': {
      marginRight: 19,
    },

    '@media (min-width: 1920px)': {
      width: 'calc(33% - 12px)',

      '&:nth-child(2n+1)': {
        marginRight: 0,
      },

      '&:nth-child(3n+1)': {
        marginRight: 19,
      },

      '&:nth-child(3n+2)': {
        marginRight: 19,
      },
    },
  },
}));

export default function DashboardRelease() {
  const classes = useStyles();
  const { t } = useTranslation();

  const createReleaseModalRef = useRef(null);
  const viewReleaseModalRef = useRef(null);

  const [queryReleaseNotes, { data, loading }] = useLazyQuery(QUERY_RELEASE_NOTES, {
    fetchPolicy: 'network-only',
  });

  const [deleteReleaseNote, { loading: deleteLoading }] = useMutation(
    MUTATION_DELETE_RELEASE_NOTE,
    {
      fetchPolicy: 'network-only',
      onCompleted: res => {
        if (res?.deleteReleaseNote?.success) {
          if (viewReleaseModalRef.current?.visible) {
            viewReleaseModalRef.current?.handleCloseModal();
          }

          queryReleaseNotes();
        }
      },
    },
  );

  const [changePublishedAt, { loading: changeLoading }] = useMutation(
    MUTATION_CHANGE_PUBLISHED_AT,
    {
      fetchPolicy: 'network-only',
      onCompleted: res => {
        if (res?.scheduleReleaseNote?.success) {
          if (viewReleaseModalRef.current?.visible) {
            viewReleaseModalRef.current?.handleCloseModal();
          }

          queryReleaseNotes();
        }
      },
    },
  );

  const dataSource = data?.listReleaseNotes?.results ?? [];

  const handleAddClick = () => {
    createReleaseModalRef.current?.handleOpenModal();
  };

  const handleViewClick = row => {
    viewReleaseModalRef.current?.handleOpenModal(row);
  };

  const handleRemoveClick = id => {
    deleteReleaseNote({
      variables: {
        input: {
          releaseNoteId: id,
        },
      },
    });
  };

  const handleChangeSchedule = ({ id, publishedAt }) => {
    changePublishedAt({
      variables: {
        input: {
          releaseNoteId: id,
          publishedAt,
        },
      },
    });
  };

  useEffect(() => {
    queryReleaseNotes();
  }, []);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h2 className={classes.title}>{t('content.release_note.title')}</h2>

        <If condition={dataSource.length > 0}>
          <Button type="primary" onClick={handleAddClick}>
            <PlusOutlined />
            {t('content.release_note.btn.add')}
          </Button>
        </If>
      </div>

      <div className={classes.content}>
        <Choose>
          <When condition={loading || deleteLoading || changeLoading}>
            <Row gutter={19}>
              <Col span={12}>
                <Skeleton active className={classes.skeleton} />
              </Col>
              <Col span={12}>
                <Skeleton active className={classes.skeleton} />
              </Col>
              <Col span={12}>
                <Skeleton active className={classes.skeleton} />
              </Col>
              <Col span={12}>
                <Skeleton active className={classes.skeleton} />
              </Col>
            </Row>
          </When>
          <When condition={!dataSource.length}>
            <div className={classes.empty}>
              <EmptyReleaseNote />
              <div className={classes.emptySummary}>{t('content.release_note.empty.notice')}</div>
              <Button type="primary" onClick={handleAddClick}>
                <PlusOutlined />
                {t('content.release_note.btn.add')}
              </Button>
            </div>
          </When>
          <Otherwise>
            <Row className={classes.articleList}>
              <For of={dataSource} each="release">
                <Col className={classes.articleItem}>
                  <Card
                    data={release}
                    handleViewClick={handleViewClick}
                    handleRemoveClick={handleRemoveClick}
                  />
                </Col>
              </For>
            </Row>
          </Otherwise>
        </Choose>
      </div>

      <CreateReleaseModal
        wrappedComponentRef={form => (createReleaseModalRef.current = form)}
        refetchData={queryReleaseNotes}
      />

      <ViewReleaseModal
        ref={viewReleaseModalRef}
        handleRemoveClick={handleRemoveClick}
        handleChangeSchedule={handleChangeSchedule}
      />
    </div>
  );
}
